import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, LogOut, MessageSquareText, ShieldCheck } from "lucide-react";
import { Brand } from "@/components/Brand";
import { useApp } from "@/context/AppContext";
import { apiError, http } from "@/lib/api";

// New owners confirm a mobile number once, right after signing up. Nobody else sees this page.
export default function VerifyPhone() {
  const { user, applyUser, logout } = useApp();
  const nav = useNavigate();
  // A refresh mid-way keeps the code step if a text was already sent.
  const pending = user?.pending_phone || "";
  const [step, setStep] = useState(pending ? "code" : "phone");
  const [phone, setPhone] = useState(pending.startsWith("+91") ? pending.slice(3) : "");
  const [masked, setMasked] = useState(pending ? pending.slice(0, 3) + " ••••••" + pending.slice(-4) : "");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(0);
  const codeBox = useRef(null);

  useEffect(() => {
    if (wait <= 0) return undefined;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);
  useEffect(() => { if (step === "code") codeBox.current?.focus(); }, [step]);

  const digits = phone.replace(/\D/g, "");
  const phoneOk = /^[6-9]\d{9}$/.test(digits);

  const sendCode = async (e) => {
    e?.preventDefault();
    if (busy || !phoneOk) return;
    setError(""); setBusy(true);
    try {
      const { data } = await http.post("/auth/phone/start", { phone: digits });
      setMasked(data.phone); setWait(data.resend_after || 30); setCode(""); setStep("code");
    } catch (err) {
      setError(apiError(err));
    } finally { setBusy(false); }
  };

  const submitCode = async (value) => {
    if (busy || value.length !== 6) return;
    setError(""); setBusy(true);
    try {
      const { data } = await http.post("/auth/phone/check", { code: value });
      applyUser(data.user);
      nav("/setup", { replace: true });
    } catch (err) {
      setError(apiError(err)); setCode("");
      codeBox.current?.focus();
    } finally { setBusy(false); }
  };

  const onCode = (e) => {
    const v = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(v);
    if (v.length === 6) submitCode(v); // finishes by itself once the sixth digit is in
  };

  const changeNumber = () => { setStep("phone"); setCode(""); setError(""); setWait(0); };

  return (
    <div className="grid min-h-screen min-h-dvh place-items-center px-5 py-10" data-testid="verify-phone-page">
      <div className="w-full max-w-md">
        <div className="mb-8"><Brand tagline /></div>
        <div className="dm-panel p-6 md:p-8">
          <span className="grid h-11 w-11 place-items-center rounded-full bg-[var(--dm-accent-soft)] dm-accent-text">
            {step === "phone" ? <ShieldCheck size={20} /> : <MessageSquareText size={20} />}
          </span>

          {step === "phone" ? (
            <form onSubmit={sendCode} data-testid="phone-step">
              <h1 className="mt-4 font-head text-xl font-bold tracking-tight text-slate-900">Verify your mobile number</h1>
              <p className="mt-1.5 text-sm text-slate-600">
                We'll text you a 6-digit code to confirm it's really you. You only do this once.
              </p>
              <label className="mt-6 block">
                <span className="label-xs">Mobile number</span>
                <div className="mt-1.5 flex gap-2">
                  <span className="grid shrink-0 place-items-center rounded-[10px] border border-slate-200 bg-slate-50 px-3 text-[14px] font-semibold text-slate-600">+91</span>
                  <input data-testid="phone-input" className="dm-input font-mono tracking-wide" type="tel" inputMode="numeric" autoComplete="tel-national"
                    maxLength={14} placeholder="98765 43210" value={phone} autoFocus onChange={(e) => setPhone(e.target.value.replace(/[^\d\s]/g, ""))} />
                </div>
              </label>
              {phone && !phoneOk && digits.length >= 10 && <p className="mt-2 text-xs text-slate-500">Indian mobile numbers are 10 digits and start with 6, 7, 8 or 9.</p>}
              {error && <p data-testid="phone-error" role="alert" className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm font-medium text-red-700">{error}</p>}
              <button type="submit" data-testid="phone-send" disabled={busy || !phoneOk} className="dm-btn-primary mt-6 w-full !py-3 disabled:opacity-40">
                {busy ? "Sending…" : "Send code"} <ArrowRight size={16} />
              </button>
            </form>
          ) : (
            <div data-testid="code-step">
              <h1 className="mt-4 font-head text-xl font-bold tracking-tight text-slate-900">Enter your code</h1>
              <p className="mt-1.5 text-sm text-slate-600">
                We texted a 6-digit code to <span className="font-semibold text-slate-800" data-testid="phone-masked">{masked}</span>.
              </p>
              <input ref={codeBox} data-testid="code-input" aria-label="6-digit code" className="dm-input mt-6 text-center font-mono text-2xl tracking-[0.5em]"
                inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="••••••" value={code} onChange={onCode} disabled={busy} />
              {error && <p data-testid="phone-error" role="alert" className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm font-medium text-red-700">{error}</p>}
              <button type="button" data-testid="code-submit" onClick={() => submitCode(code)} disabled={busy || code.length !== 6} className="dm-btn-primary mt-6 w-full !py-3 disabled:opacity-40">
                {busy ? "Checking…" : "Verify"} <ArrowRight size={16} />
              </button>
              <div className="mt-3 flex items-center justify-between text-sm">
                <button type="button" data-testid="phone-change" onClick={changeNumber} className="dm-link">Use a different number</button>
                {wait > 0
                  ? <span className="text-slate-500" data-testid="resend-wait">Resend in {wait}s</span>
                  : <button type="button" data-testid="code-resend" onClick={sendCode} disabled={busy || !phoneOk} className="dm-link disabled:opacity-40"
                      title={phoneOk ? "" : "Use a different number to send to a new one"}>Resend code</button>}
              </div>
              {!phoneOk && wait <= 0 && <p className="mt-2 text-xs text-slate-500">To get another code, tap “Use a different number” and enter the number again.</p>}
            </div>
          )}
        </div>
        <button type="button" data-testid="phone-logout" onClick={() => { logout(); nav("/login", { replace: true }); }}
          className="dm-btn-quiet mx-auto mt-5 flex"><LogOut size={14} /> Sign out</button>
      </div>
    </div>
  );
}
